import { useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Send, Loader2 } from "lucide-react";

/**
 * Generic chat component.
 * Props:
 *  - fetchMessages: () => Promise<Message[]>
 *  - sendMessage: (content: string) => Promise<Message>
 *  - currentRole: 'client' | 'admin'
 *  - placeholder?: string
 *  - emptyText?: string
 */
export default function ChatWindow({ fetchMessages, sendMessage, currentRole, placeholder = "Écrire un message…", emptyText }) {
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const msgs = await fetchMessages();
      setMessages(msgs);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [fetchMessages]);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSending(true);
    setError("");
    try {
      const newMsg = await sendMessage(content.trim());
      setMessages((prev) => [...prev, newMsg]);
      setContent("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#FAFAFA] border border-[#E5E3DD]" data-testid="chat-window">
      <div className="flex-1 overflow-y-auto p-5 space-y-3 no-scrollbar" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center text-[#5C5A56] text-sm py-12">{emptyText || "Aucun message pour l'instant. Envoyez le premier !"}</div>
        )}
        {messages.map((m) => {
          const mine = m.sender_role === currentRole;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-2.5 text-sm ${mine ? "chat-bubble-client" : "chat-bubble-admin"}`} data-testid={`chat-message-${m.id}`}>
                <div className="whitespace-pre-wrap break-words">{m.content}</div>
                <div className={`text-[10px] mt-1 opacity-60 ${mine ? "text-right" : "text-left"}`}>
                  {new Date(m.created_at).toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {error && (
        <div data-testid="chat-error" className="px-4 py-2 text-xs text-red-700 bg-red-50 border-t border-red-200">{error}</div>
      )}

      <form onSubmit={submit} className="border-t border-[#E5E3DD] p-3 flex items-center gap-2" data-testid="chat-form">
        <input
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-[#F3F1ED] px-4 py-3 border border-transparent focus:border-[#C86641] focus:outline-none transition-colors"
          data-testid={currentRole === "admin" ? "admin-chat-input" : "chat-input"}
        />
        <button
          type="submit"
          disabled={sending || !content.trim()}
          data-testid={currentRole === "admin" ? "admin-chat-send" : "chat-send-button"}
          className="bg-[#C86641] text-[#FAFAFA] px-5 py-3 font-semibold hover:bg-[#A65132] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
        >
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </form>
    </div>
  );
}

// Helper API hooks
export const chatApi = {
  myMessages: () => api.get("/chat/my-conversation/messages").then((r) => r.data),
  myConversation: () => api.get("/chat/my-conversation").then((r) => r.data),
  sendMine: (content) => api.post("/chat/my-conversation/messages", { content }).then((r) => r.data),
  listConversations: () => api.get("/chat/conversations").then((r) => r.data),
  conversationMessages: (id) => api.get(`/chat/conversations/${id}/messages`).then((r) => r.data),
  sendAdmin: (id, content) => api.post(`/chat/conversations/${id}/messages`, { content }).then((r) => r.data),
};
